const STORAGE_KEY = "hydrotools:client-project-context:v1";

const BOOTSTRAP = {
  clients: [
    { id: "safeharbor", name: "Safe Harbor Marinas", slug: "safeharbor", createdAt: "2026-01-01T00:00:00.000Z" },
  ],
  projects: [
    {
      id: "llm", clientId: "safeharbor", name: "LLM Marina", slug: "llm",
      horizontalCRS: "EPSG:2227", horizontalUnits: "ftUS",
      verticalDatum: "MLLW", verticalUnits: "ft",
      geoidModel: "GEOID18", marinaId: "LLM",
      s3Prefix: "clients/safeharbor/llm",
      location: { lat: 37.8715, lng: -122.3375, zoom: 16 },
      createdAt: "2026-01-01T00:00:00.000Z",
    },
    {
      id: "htm", clientId: "safeharbor", name: "HTM Marina", slug: "htm",
      horizontalCRS: "EPSG:2236", horizontalUnits: "ftUS",
      verticalDatum: "MLLW", verticalUnits: "ft",
      geoidModel: "GEOID18", marinaId: "HTM",
      s3Prefix: "clients/safeharbor/htm",
      location: { lat: 24.5611, lng: -81.7998, zoom: 16 },
      createdAt: "2026-01-01T00:00:00.000Z",
    },
    {
      id: "rsbc", clientId: "safeharbor", name: "RSBC Marina", slug: "rsbc",
      horizontalCRS: "EPSG:2236", horizontalUnits: "ftUS",
      verticalDatum: "MLLW", verticalUnits: "ft",
      geoidModel: "GEOID18", marinaId: "RSBC",
      s3Prefix: "clients/safeharbor/rsbc",
      location: { lat: 25.7745, lng: -80.1300, zoom: 16 },
      createdAt: "2026-01-01T00:00:00.000Z",
    },
  ],
  activeClientId: "safeharbor",
  activeProjectId: null,
};

function slugify(value, fallback = "project") {
  const slug = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug || fallback;
}

function titleFromSlug(value) {
  return String(value || "")
    .split(/[-_]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ") || String(value || "Project");
}

function clientFromPrefix(prefix) {
  const parts = String(prefix || "").split("/").filter(Boolean);
  if (parts[0] === "clients" && parts[1]) return slugify(parts[1], "client");
  return "safeharbor";
}

function normalizeClient(client) {
  const slug = slugify(client?.slug || client?.id || client?.name, "client");
  return {
    id: slug,
    slug,
    name: String(client?.name || titleFromSlug(slug)),
    createdAt: String(client?.createdAt || ""),
  };
}

function normalizeProject(project) {
  const settings = project?.settings && typeof project.settings === "object" ? project.settings : {};
  const s3Prefix = String(project?.s3Prefix || "").replace(/^\/+|\/+$/g, "");
  const clientId = slugify(project?.clientId || project?.clientSlug || clientFromPrefix(s3Prefix), "safeharbor");
  const slug = slugify(project?.slug || project?.projectSlug || project?.code || project?.id || project?.name, "project");
  const marinaId = String(project?.marinaId || project?.code || slug.toUpperCase()).trim().toUpperCase();
  const lat = Number(project?.location?.lat ?? project?.lat);
  const lng = Number(project?.location?.lng ?? project?.lng);
  return {
    id: String(project?.id || marinaId || `${clientId}-${slug}`),
    clientId,
    clientSlug: clientId,
    clientName: String(project?.clientName || titleFromSlug(clientId)),
    name: String(project?.name || titleFromSlug(slug)),
    slug,
    code: String(project?.code || marinaId),
    horizontalCRS: String(project?.horizontalCRS || settings.horizontalCRS || settings.crs || "EPSG:4326"),
    horizontalUnits: String(project?.horizontalUnits || settings.horizontalUnits || settings.linearUnits || "m"),
    verticalDatum: String(project?.verticalDatum || settings.verticalDatum || "MSL"),
    verticalUnits: String(project?.verticalUnits || settings.verticalUnits || "m"),
    geoidModel: String(project?.geoidModel || settings.geoidModel || "None"),
    marinaId,
    s3Prefix: s3Prefix || `clients/${clientId}/${slug}`,
    location: Number.isFinite(lat) && Number.isFinite(lng)
      ? { lat, lng, zoom: Number(project?.location?.zoom || 16) }
      : null,
    type: String(project?.type || project?.projectType || "marina"),
    active: project?.active !== false,
    address: String(project?.address || "").trim(),
    city: String(project?.city || "").trim(),
    state: String(project?.state || "").trim(),
    zip: String(project?.zip || "").trim(),
    phone: String(project?.phone || "").trim(),
    gmName: String(project?.gmName || "").trim(),
    gmEmail: String(project?.gmEmail || "").trim(),
    projectNumber: String(project?.projectNumber || "").trim(),
    group: String(project?.group || "").trim(),
    acquisitionDate: String(project?.acquisitionDate || "").trim(),
    createdAt: String(project?.createdAt || ""),
    updatedAt: String(project?.updatedAt || ""),
  };
}

function dedupeById(items) {
  const byId = new Map();
  for (const item of items) byId.set(item.id, { ...(byId.get(item.id) || {}), ...item });
  return [...byId.values()];
}

// sendCrs: only when the user actually chose the CRS (creating the project, or an edit that
// changes it). Activation re-registers too, and this tab's cached copy of the CRS can be stale
// -- sending it would overwrite the project's CRS with whatever the tab last saw. Without it
// the server keeps the stored CRS.
async function registerProjectWithServer(project, client, { sendCrs = false } = {}) {
  if (!project?.marinaId || !client?.slug || !project?.slug) return null;
  const resp = await fetch("/api/project/projects/register", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({
      projectId: project.marinaId,
      clientSlug: client.slug,
      clientName: client.name,
      projectSlug: project.slug,
      name: project.name,
      horizontalCRS: sendCrs ? project.horizontalCRS : undefined,
      horizontalUnits: project.horizontalUnits,
      verticalDatum: project.verticalDatum,
      verticalUnits: project.verticalUnits,
      geoidModel: project.geoidModel,
      location: project.location,
      address: project.address,
      city: project.city,
      state: project.state,
      zip: project.zip,
      phone: project.phone,
      gmName: project.gmName,
      gmEmail: project.gmEmail,
      projectNumber: project.projectNumber,
      group: project.group || null,
    }),
  });
  const payload = await resp.json().catch(() => ({}));
  if (!resp.ok || payload?.ok === false) throw new Error(payload?.error || `HTTP ${resp.status}`);
  return payload?.project ? normalizeProject(payload.project) : project;
}

export function createContextService({ events, projections }) {
  let state = load();
  let refreshPromise = null;

  // A fresh page load never comes up with a project already "active" -- see
  // getPendingResumeProject/getActiveProject below. What was active last session is only
  // ever exposed as a resumable suggestion (pendingResumeProjectId), never applied
  // automatically; it only becomes live via an explicit setActiveProject() call.
  function load() {
    try {
      const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
      if (parsed && Array.isArray(parsed?.clients) && parsed.clients.length > 0) {
        return {
          clients: dedupeById(parsed.clients.map(normalizeClient)),
          projects: dedupeById((Array.isArray(parsed?.projects) ? parsed.projects : []).map(normalizeProject)),
          activeClientId: parsed?.activeClientId || null,
          activeProjectId: null,
          pendingResumeProjectId: parsed?.activeProjectId || null,
          catalogStatus: "cached",
          catalogError: "",
        };
      }
    } catch {}
    return {
      clients: BOOTSTRAP.clients.map(normalizeClient),
      projects: BOOTSTRAP.projects.map(normalizeProject),
      activeClientId: BOOTSTRAP.activeClientId,
      activeProjectId: null,
      pendingResumeProjectId: BOOTSTRAP.activeProjectId,
      catalogStatus: "bootstrap",
      catalogError: "",
    };
  }

  function persist() {
    // Falls back to pendingResumeProjectId whenever nothing has been explicitly activated
    // yet this session (state.activeProjectId is still null) -- otherwise an early persist()
    // (e.g. from mergeCatalog, which runs before the user has had a chance to click Resume)
    // would overwrite the stored value with null and permanently lose the resume target.
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      clients: state.clients,
      projects: state.projects,
      activeClientId: state.activeClientId,
      activeProjectId: state.activeProjectId ?? state.pendingResumeProjectId,
    }));
  }

  function applyCRS(project) {
    if (!project || !projections) return;
    projections.setProjectProfile({
      name: project.name,
      horizontalCRS: project.horizontalCRS || "EPSG:4326",
      verticalDatum: project.verticalDatum || "MSL",
      geoidModel: project.geoidModel || "None",
      linearUnits: project.horizontalUnits || "meters",
      angularUnits: "degrees",
    });
  }

  function mergeCatalog(clients, projects) {
    const normalizedProjects = projects.map(normalizeProject);
    const generatedClients = normalizedProjects.map((project) => normalizeClient({
      id: project.clientId,
      slug: project.clientId,
      name: project.clientName,
      createdAt: project.createdAt,
    }));
    state.clients = dedupeById([...state.clients, ...clients.map(normalizeClient), ...generatedClients])
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
    state.projects = dedupeById([...state.projects, ...normalizedProjects])
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
    if (state.activeProjectId && !state.projects.some((p) => p.id === state.activeProjectId)) {
      state.activeProjectId = null;
    }
    if (state.activeClientId && !state.clients.some((c) => c.id === state.activeClientId)) {
      state.activeClientId = state.projects.find((p) => p.id === state.activeProjectId)?.clientId || state.clients[0]?.id || null;
    }
    persist();
  }

  async function refreshFromServer({ emit = true } = {}) {
    if (refreshPromise) return refreshPromise;
    state.catalogStatus = "loading";
    refreshPromise = (async () => {
      try {
        const resp = await fetch("/api/projects/catalog?scope=context", { headers: { Accept: "application/json" }, cache: "no-store" });
        const payload = await resp.json().catch(() => ({}));
        if (!resp.ok || payload?.ok === false) throw new Error(payload?.error || `HTTP ${resp.status}`);
        const catalog = payload?.catalog || {};
        // Replace catalog from S3 — source of truth, not additive merge
        const fetchedProjects = (Array.isArray(catalog.projects) ? catalog.projects : []).map(normalizeProject);
        const fetchedClients = (Array.isArray(catalog.clients) ? catalog.clients : []).map(normalizeClient);
        const derivedClients = fetchedProjects.map((p) => normalizeClient({ id: p.clientId, slug: p.clientId, name: p.clientName, createdAt: p.createdAt }));
        state.projects = dedupeById(fetchedProjects).sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
        // Preserve locally-created clients that haven't appeared in S3 yet (race condition:
        // an in-flight refresh can start before the POST /api/clients write completes).
        // Keep them for up to 30 s, then let subsequent refreshes drop them if still absent.
        const freshIds = new Set([...fetchedClients, ...derivedClients].map((c) => c.id));
        const nowMs = Date.now();
        const pendingClients = state.clients.filter(
          (c) => !freshIds.has(c.id) && c.createdAt && (nowMs - new Date(c.createdAt).getTime()) < 30_000
        );
        state.clients = dedupeById([...fetchedClients, ...derivedClients, ...pendingClients]).sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
        if (state.activeProjectId && !state.projects.some((p) => p.id === state.activeProjectId)) state.activeProjectId = null;
        if (state.activeClientId && !state.clients.some((c) => c.id === state.activeClientId)) {
          state.activeClientId = state.projects.find((p) => p.id === state.activeProjectId)?.clientId || state.clients[0]?.id || null;
        }
        persist();
        state.catalogStatus = "synced";
        state.catalogError = "";
        applyCRS(getActiveProject());
        if (emit) events.emit("context:catalog:changed", { clients: getClients(), projects: getAllProjects() });
      } catch (err) {
        state.catalogStatus = "error";
        state.catalogError = String(err?.message || err);
        if (emit) events.emit("context:catalog:error", { error: state.catalogError });
      } finally {
        refreshPromise = null;
      }
      return { clients: getClients(), projects: getAllProjects(), status: state.catalogStatus, error: state.catalogError };
    })();
    return refreshPromise;
  }

  function getClients() { return [...state.clients]; }
  function getAllProjects() { return [...state.projects]; }
  function getProjects(clientId) { return state.projects.filter((p) => p.clientId === clientId); }
  function getCatalogStatus() { return { status: state.catalogStatus, error: state.catalogError }; }
  function getActiveClient() { return state.clients.find((c) => c.id === state.activeClientId) || null; }
  function getActiveProject() { return state.projects.find((p) => p.id === state.activeProjectId) || null; }
  // What was active last session, offered as a "Resume" suggestion -- never applied
  // automatically. Re-validated against the current catalog every call (same staleness check
  // mergeCatalog/refreshFromServer apply to activeProjectId) so a since-deleted project isn't
  // offered.
  function getPendingResumeProject() { return state.projects.find((p) => p.id === state.pendingResumeProjectId) || null; }

  function setActiveProject(projectId) {
    const project = state.projects.find((p) => p.id === projectId);
    if (!project) return;
    state.activeProjectId = projectId;
    state.activeClientId = project.clientId;
    persist();
    applyCRS(project);
    const client = getActiveClient();
    registerProjectWithServer(project, client).catch((err) => {
      state.catalogStatus = "error";
      state.catalogError = String(err?.message || err);
      events.emit("context:catalog:error", { error: state.catalogError });
    });
    events.emit("context:changed", { client, project });
    if (project.marinaId) {
      document.dispatchEvent(new CustomEvent("hydrotools:project-changed", {
        detail: { projectId: project.marinaId, fromContext: true, horizontalCRS: project.horizontalCRS || "" },
      }));
    }
  }

  async function createClient({ name }) {
    const slug = slugify(name, `client-${Date.now()}`);
    const draft = normalizeClient({ id: slug, name, slug, createdAt: new Date().toISOString() });
    const resp = await fetch("/api/clients", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ name: draft.name, clientSlug: draft.slug }),
    });
    const payload = await resp.json().catch(() => ({}));
    if (!resp.ok || payload?.ok === false) throw new Error(payload?.error || `HTTP ${resp.status}`);
    const saved = payload.client ? normalizeClient(payload.client) : draft;
    state.clients = dedupeById([...state.clients, saved]);
    state.activeClientId = saved.id;
    persist();
    events.emit("context:catalog:changed", { clients: getClients(), projects: getAllProjects() });
    // Wait for any in-flight refresh to settle, then re-sync so the new client.json in S3
    // isn't missed by a catalog fetch that started before the POST completed.
    (refreshPromise || Promise.resolve()).then(() => refreshFromServer({ emit: true }));
    return saved;
  }

  async function createProject(data) {
    const { clientId, name, horizontalCRS, horizontalUnits, verticalDatum, verticalUnits, geoidModel, location } = data;
    const client = state.clients.find((c) => c.id === clientId) || null;
    if (!client) throw new Error("Select a client before creating a project.");
    const slug = slugify(name, `project-${Date.now()}`);
    const project = normalizeProject({
      id: slug.toUpperCase(),
      clientId,
      clientName: client.name,
      name,
      slug,
      code: slug.toUpperCase(),
      marinaId: slug.toUpperCase(),
      s3Prefix: `clients/${client.slug}/${slug}`,
      horizontalCRS: horizontalCRS || "EPSG:4326",
      horizontalUnits: horizontalUnits || "m",
      verticalDatum: verticalDatum || "MSL",
      verticalUnits: verticalUnits || "m",
      geoidModel: geoidModel || "None",
      location: location || null,
      createdAt: new Date().toISOString(),
    });
    const saved = await registerProjectWithServer(project, client, { sendCrs: true });
    state.projects = dedupeById([...state.projects, saved]);
    state.activeClientId = saved.clientId;
    state.catalogStatus = "synced";
    state.catalogError = "";
    persist();
    events.emit("context:catalog:changed", { clients: getClients(), projects: getAllProjects() });
    return saved;
  }

  function updateProject(projectId, updates) {
    const idx = state.projects.findIndex((p) => p.id === projectId);
    if (idx < 0) return null;
    state.projects[idx] = normalizeProject({ ...state.projects[idx], ...updates });
    persist();
    const client = state.clients.find((c) => c.id === state.projects[idx].clientId) || null;
    registerProjectWithServer(state.projects[idx], client, { sendCrs: "horizontalCRS" in (updates || {}) }).catch((err) => {
      state.catalogStatus = "error";
      state.catalogError = String(err?.message || err);
      events.emit("context:catalog:error", { error: state.catalogError });
    });
    if (state.activeProjectId === projectId) {
      events.emit("context:changed", { client: getActiveClient(), project: state.projects[idx] });
    }
    events.emit("context:catalog:changed", { clients: getClients(), projects: getAllProjects() });
    return state.projects[idx];
  }

  function deleteProject(projectId) {
    state.projects = state.projects.filter((p) => p.id !== projectId);
    if (state.activeProjectId === projectId) {
      state.activeProjectId = null;
      events.emit("context:changed", { client: getActiveClient(), project: null });
    }
    persist();
    events.emit("context:catalog:changed", { clients: getClients(), projects: getAllProjects() });
  }

  function deleteClient(clientId) {
    state.clients = state.clients.filter((c) => c.id !== clientId);
    state.projects = state.projects.filter((p) => p.clientId !== clientId);
    if (state.activeClientId === clientId) {
      state.activeClientId = state.clients[0]?.id || null;
      state.activeProjectId = null;
      events.emit("context:changed", { client: null, project: null });
    }
    persist();
    events.emit("context:catalog:changed", { clients: getClients(), projects: getAllProjects() });
  }

  persist();
  applyCRS(getActiveProject());
  refreshFromServer({ emit: true });

  // Re-sync whenever the user returns to the tab or every 3 minutes
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") refreshFromServer({ emit: true });
  });
  setInterval(() => refreshFromServer({ emit: true }), 3 * 60 * 1000);

  function applyActiveCRS() {
    applyCRS(getActiveProject());
  }

  function setActiveClient(clientId) {
    const client = state.clients.find((c) => c.id === clientId);
    if (!client) return;
    state.activeClientId = clientId;
    const currentProject = getActiveProject();
    if (currentProject && currentProject.clientId !== clientId) {
      state.activeProjectId = null;
      persist();
      events.emit("context:changed", { client, project: null });
    } else {
      persist();
      events.emit("context:changed", { client, project: currentProject || null });
    }
  }

  return {
    getClients,
    getProjects,
    getAllProjects,
    getActiveClient,
    getActiveProject,
    getPendingResumeProject,
    getCatalogStatus,
    refreshFromServer,
    setActiveProject,
    createClient,
    createProject,
    updateProject,
    deleteProject,
    deleteClient,
    applyActiveCRS,
    setActiveClient,
  };
}
